import { useSyncExternalStore } from "react";

/**
 * Enkelt varslingssenter. Varsler (f.eks. nye salg) skrives hit og vises i
 * bjella i sidemenyen. Lagres i localStorage per nettleser nå; flyttes til en
 * server-kilde (per bruker) når Entra SSO er på plass.
 */
export type VarselType = "salg" | "info";
export type Varsel = {
  id: string; // unik og stabil, f.eks. "sale:<saleId>" (brukes til dedupe)
  type: VarselType;
  tittel: string;
  tekst?: string;
  lenke?: string; // rute å navigere til ved klikk
  tid: number; // epoch ms
  lest: boolean;
};

const KEY = "gb.varsler";
const MAX = 100;

const listeners = new Set<() => void>();
let snapshot: Varsel[] = les();

function les(): Varsel[] {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? (JSON.parse(raw) as Varsel[]) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function skriv(arr: Varsel[]): void {
  // nyeste først, og kutt til MAX for å unngå ubegrenset vekst
  const sortert = [...arr].sort((a, b) => b.tid - a.tid).slice(0, MAX);
  snapshot = sortert;
  try {
    localStorage.setItem(KEY, JSON.stringify(sortert));
  } catch {
    /* ignorer – varsling er ikke kritisk */
  }
  listeners.forEach((l) => l());
}

export function hentVarsler(): Varsel[] {
  return snapshot;
}

export function antallUlest(): number {
  return snapshot.reduce((n, v) => n + (v.lest ? 0 : 1), 0);
}

export function leggTilVarsel(
  v: Omit<Varsel, "lest" | "tid"> & { tid?: number }
): void {
  if (snapshot.some((x) => x.id === v.id)) return; // dedupe på stabil id
  skriv([...snapshot, { ...v, tid: v.tid ?? Date.now(), lest: false }]);
}

export function markerLest(id: string): void {
  skriv(snapshot.map((v) => (v.id === id ? { ...v, lest: true } : v)));
}

export function markerAlleLest(): void {
  skriv(snapshot.map((v) => (v.lest ? v : { ...v, lest: true })));
}

export function fjernAlle(): void {
  skriv([]);
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** React-hook: abonnerer på varsler og re-renderer ved endring. */
export function useVarsler(): { varsler: Varsel[]; ulest: number } {
  const varsler = useSyncExternalStore(subscribe, hentVarsler, hentVarsler);
  const ulest = varsler.reduce((n, v) => n + (v.lest ? 0 : 1), 0);
  return { varsler, ulest };
}

/** «3 min siden», «2 t siden», «i går» … kort relativ tid på norsk. */
export function tidSiden(t: number): string {
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "nå";
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)} min siden`;
  const h = m / 60;
  if (h < 24) return `${Math.floor(h)} t siden`;
  const d = h / 24;
  if (d < 2) return "i går";
  return `${Math.floor(d)} d siden`;
}
